import { HgProductCategory } from "../../../../../legacy/types/companySearch/MI";
import { isEmpty } from "../../../../../utils/jsUtils";
import { getAllPossibleDescendantIds } from "../../utils";
import { useTaxonomyContext } from "../../../../../contexts/Taxonomy/TaxonomyContext";

const generateLabel = (
  selectedCategoryIds: string[],
  allCategories: HgProductCategory[]
): string => {
  if (isEmpty(selectedCategoryIds)) {
    return "All";
  }

  const selectedIdsSet = new Set(selectedCategoryIds);
  const categoriesById = new Map<string, HgProductCategory>();
  const childrenByParentId = new Map<string, string[]>();

  for (const category of allCategories) {
    categoriesById.set(category.id, category);
    if (category.parentId) {
      // Ensure parentId exists
      if (!childrenByParentId.has(category.parentId)) {
        childrenByParentId.set(category.parentId, []);
      }
      childrenByParentId.get(category.parentId)!.push(category.id);
    }
  }

  const summarizingRoots: HgProductCategory[] = [];

  for (const selectedId of selectedIdsSet) {
    const category = categoriesById.get(selectedId);
    if (!category) continue;

    const { parentId } = category;
    if (parentId === "0" || !selectedIdsSet.has(parentId || "")) {
      const allPossibleDescendants = getAllPossibleDescendantIds(
        category.id,
        childrenByParentId,
        categoriesById
      );

      let isEntireSubtreeSelected = true;
      if (
        allPossibleDescendants.size === 0 &&
        categoriesById.has(category.id)
      ) {
        // This case means category.id itself has no children or wasn't processed by getAllPossibleDescendantIds.
        // If it's a leaf, it's selected. Check if it's in selectedIdsSet.
        if (!selectedIdsSet.has(category.id)) {
          isEntireSubtreeSelected = false;
        }
      } else if (allPossibleDescendants.size > 0) {
        for (const descendantId of allPossibleDescendants) {
          if (!selectedIdsSet.has(descendantId)) {
            isEntireSubtreeSelected = false;
            break;
          }
        }
      } else {
        isEntireSubtreeSelected = false;
      }

      if (isEntireSubtreeSelected && allPossibleDescendants.size > 0) {
        summarizingRoots.push(category);
      }
    }
  }

  if (summarizingRoots.length === 1) {
    return summarizingRoots[0].name;
  }

  if (summarizingRoots.length > 1) {
    return `${summarizingRoots.length} selected`;
  }

  if (selectedIdsSet.size === 1) {
    const [singleSelectedId] = selectedCategoryIds;
    const singleCategory = categoriesById.get(singleSelectedId);
    return singleCategory ? singleCategory.name : "1 selected";
  }

  return `${selectedIdsSet.size} selected`;
};

export const useProductCategoriesLabel = (selectedIds: string[]): string => {
  const { productCategories } = useTaxonomyContext();

  if (!productCategories) {
    return isEmpty(selectedIds) ? "All" : `${selectedIds.length} selected`;
  }

  return generateLabel(selectedIds, productCategories);
};
